<script setup lang="ts">
import type { EtatDonCagnotte } from "./PageCagnotteEtatDon.vue";
/**
 * PAGE CAGNOTTE — barre du bas sur TÉLÉPHONE, quand la carte de don n'est pas à
 * l'écran (Karim 2026-10-13). Même logique que la barre de l'ancienne page
 * (`CampaignStickyBar`, qui reste telle quelle là-bas), habillée comme la
 * nouvelle carte de don : anneau de progression et montant collecté en
 * version compacte, plus gros don (comme l'ancienne barre), « Faire un don »
 * de la couleur des boutons rapides, bouton rond « Partager ».
 *
 * « Faire un don » TRANSFORME la barre en panneau (Karim 2026-10-13) : le
 * panneau de don (`PageCagnotteClavierDon` : montant en grand, montants rapides,
 * clavier) part du bord haut de la barre et monte jusqu'à sa place, pendant
 * que la barre s'efface — on dirait que la barre grandit.
 * Fermeture : croix, fond assombri, Échap ou glisser vers le bas.
 *
 * ⚡ FLUIDITÉ : seuls `transform` et `opacity` sont animés. Le téléphone les
 * anime sur la carte graphique, sans recalculer la mise en page ni repeindre
 * (avant : hauteur, marges et coins animés + flou d'arrière-plan → saccades).
 */
import type { TopDonor } from "~/types/campaign";
import type { ImpactOption } from "~/components/donate/DonateImpactOptions.vue";
import PageCagnotteClavierDon from "./PageCagnotteClavierDon.vue";
import type { CagnotteAffichee } from "./types";
import { useDemoAccueil } from "~/composables/useDemoAccueil";

const props = defineProps<{
  cagnotte: CagnotteAffichee;
  visible: boolean;
  couleur?: string | null;
  /** Plus gros don de la cagnotte (nom masqué s'il est anonyme). */
  plusGrosDon?: TopDonor | null;
  formatAmountShort: (amount: number) => string;
  /** Montants compacts « 12,3k € » (même format que l'ancienne barre). */
  formatAmountCompact: (amount: number | null | undefined) => string;
  enPause?: boolean;
  terminee?: boolean;
  /** Dons fermés (terminée / en pause / brouillon) : la barre l'annonce. */
  etatDon?: EtatDonCagnotte | null;
  /** Jour de fin en clair, pour une cagnotte terminée. */
  dateFin?: string | null;
  /** Réglage « Afficher le montant collecté » (faux : ni montant, ni anneau, ni plus gros don). */
  afficherMontant?: boolean;
  /** Date limite : même barre que la carte de don, en version resserrée. */
  closesAt?: string | null;
  endDateLabel?: string | null;
  /** Pour le panneau de don : verdict fiscal et montants de la page de paiement. */
  fiscal: boolean;
  montants: number[];
  indexDefaut: number;
  impacts: ImpactOption[];
}>();
const emit = defineEmits<{ partager: [] }>();

const { t } = useI18n();
const { estIntegree } = useDemoAccueil();

const objectif = computed(() => Number(props.cagnotte.goal_amount) || 0);
const collecte = computed(() => Number(props.cagnotte.raised) || 0);
const pourcent = computed(() =>
  objectif.value > 0 ? Math.round((collecte.value / objectif.value) * 100) : 0,
);

// Couleur des boutons rapides de montant (comme la carte de don).
const BLEU_FONCE = "var(--color-main2)";
const BLEU_CLAIR = "var(--color-main)";
const styleBoutonDon = computed(() => ({
  background: props.couleur || "var(--color-main)",
}));

// Anneau (64 px, trait 8), rempli à chaque apparition de la barre.
const ANNEAU = 64;
const TRAIT = 8;
const RAYON = (ANNEAU - TRAIT) / 2;
const CIRCONFERENCE = 2 * Math.PI * RAYON;
const remplissage = ref(0);
const decalage = computed(() => CIRCONFERENCE - (Math.min(100, remplissage.value) / 100) * CIRCONFERENCE);
watch(
  () => props.visible,
  (v) => {
    remplissage.value = 0;
    if (v) requestAnimationFrame(() => requestAnimationFrame(() => (remplissage.value = pourcent.value)));
  },
);
watch(pourcent, (v) => {
  if (props.visible) remplissage.value = v;
});

// ─── Barre → panneau (transform + opacity uniquement) ───────────────────
const ouvert = ref(false);
const barre = ref<HTMLElement | null>(null);
const panneau = ref<HTMLElement | null>(null);
/**
 * Fermé, le panneau (posé en bas de l'écran à sa taille finale) est descendu
 * pour que son bord haut tombe sur celui de la barre, et rétréci à la largeur
 * de la barre : c'est de là qu'il part et là qu'il revient.
 */
const depart = ref({ y: 0, echelle: 1 });
const glisse = ref(0);

function mesurerDepart() {
  const p = panneau.value;
  if (!p) return;
  const hauteur = p.offsetHeight;
  const b = barre.value?.getBoundingClientRect();
  const hautBarre = b ? b.top : window.innerHeight - 220;
  depart.value = {
    y: Math.max(0, hauteur - (window.innerHeight - hautBarre)),
    echelle: b && window.innerWidth ? b.width / window.innerWidth : 0.94,
  };
}
let observateur: ResizeObserver | null = null;
watch(panneau, (p) => {
  observateur?.disconnect();
  if (!p || typeof ResizeObserver === "undefined") return;
  observateur = new ResizeObserver(() => {
    if (!ouvert.value) mesurerDepart();
  });
  observateur.observe(p);
  mesurerDepart();
});

const COURBE = "cubic-bezier(0.32, 0.72, 0, 1)";
const stylePanneau = computed(() => {
  if (glisse.value) return { transform: `translate3d(0, ${glisse.value}px, 0)`, opacity: 1, transition: "none" };
  return ouvert.value
    ? {
        transform: "translate3d(0, 0, 0) scale(1, 1)",
        opacity: 1,
        transition: `transform 460ms ${COURBE}, opacity 180ms ease-out`,
      }
    : {
        transform: `translate3d(0, ${depart.value.y}px, 0) scale(${depart.value.echelle}, 1)`,
        opacity: 0,
        // En fermant : il redescend d'abord, puis s'efface sur la barre.
        transition: `transform 420ms ${COURBE}, opacity 200ms ease-in 220ms`,
      };
});
/** La barre s'efface vite à l'ouverture, revient quand le panneau la rejoint. */
const styleBarre = computed(() => ({
  bottom: "calc(0.75rem + env(safe-area-inset-bottom))",
  opacity: ouvert.value ? 0 : 1,
  transition: ouvert.value ? "opacity 140ms ease-out" : "opacity 200ms ease-out 260ms",
}));

function ouvrir() {
  if (props.etatDon) return;
  mesurerDepart();
  // Position de départ appliquée, PUIS montée (sinon pas d'animation).
  requestAnimationFrame(() => (ouvert.value = true));
}
function fermer() {
  glisse.value = 0;
  ouvert.value = false;
}
function lache(dy: number) {
  if (dy > 90) fermer();
  else glisse.value = 0;
}
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape" && ouvert.value) fermer();
}
// Page figée sous le panneau ouvert.
watch(ouvert, (v) => {
  document.body.style.overflow = v ? "hidden" : "";
});
// Démo de la home : fond et panneau passent dans <body> (cf. template). Rendus
// par le serveur, ils atterriraient tout en haut du HTML de la home, hors de
// la démo — et Vue les y chercherait au démarrage de la démo, bien après celui
// de la home. Ils n'existent donc qu'une fois la démo démarrée (le panneau
// est fermé et invisible d'ici là : rien ne change à l'écran).
const panneauPret = ref(!estIntegree);
onMounted(() => {
  window.addEventListener("keydown", onKey);
  panneauPret.value = true;
});
// La page ouvre le panneau depuis ses autres boutons « Faire un don ».
defineExpose({ ouvrir });
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKey);
  observateur?.disconnect();
  document.body.style.overflow = "";
});
</script>

<template>
  <!-- Fond assombri derrière le panneau ouvert. Démo de la home : fond et
       panneau passent au niveau de la page entière (sinon, enfermés dans
       l'écran simulé, plus petit que le téléphone, le panneau serait coupé). -->
  <Teleport v-if="panneauPret" to="body" :disabled="!estIntegree">
    <Transition enter-active-class="transition-opacity duration-300" enter-from-class="opacity-0" leave-active-class="transition-opacity duration-300" leave-to-class="opacity-0">
      <div v-if="ouvert" class="lg:hidden fixed inset-0 z-[10010] bg-black/40" @click="fermer" />
    </Transition>
  </Teleport>

  <template v-if="!terminee && !enPause || etatDon">
    <!-- BARRE -->
    <Transition
      enter-active-class="transition-[translate,opacity] duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-6"
      leave-active-class="transition-[translate,opacity] duration-200 ease-in"
      leave-to-class="opacity-0 translate-y-6"
    >
      <div
        v-if="visible || ouvert"
        ref="barre"
        class="barre-don lg:hidden fixed inset-x-3 overflow-hidden rounded-[28px] bg-white p-3.5 ring-1 ring-black/[0.06]"
        :class="ouvert ? 'pointer-events-none' : ''"
        :style="styleBarre"
        :inert="ouvert"
      >
        <!-- Dons fermés (Karim 2026-10-15) : la barre reste telle quelle,
             VOILÉE (flou + gris), et l'état s'affiche par-dessus. -->
        <div :class="etatDon ? 'contenu-voile' : ''" :inert="!!etatDon" :aria-hidden="!!etatDon || undefined">
        <div class="flex items-center gap-3">
        <!-- Anneau de progression -->
        <div v-if="objectif > 0 && afficherMontant !== false" class="relative shrink-0">
          <svg :width="ANNEAU" :height="ANNEAU" class="-rotate-90" aria-hidden="true">
            <defs>
              <linearGradient id="page-cagnotte-barre-anneau" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" :style="{ stopColor: BLEU_FONCE }" />
                <stop offset="100%" :style="{ stopColor: BLEU_CLAIR }" />
              </linearGradient>
            </defs>
            <circle :cx="ANNEAU / 2" :cy="ANNEAU / 2" :r="RAYON" stroke="#e6eefb" :stroke-width="TRAIT" fill="none" />
            <circle
              :cx="ANNEAU / 2"
              :cy="ANNEAU / 2"
              :r="RAYON"
              :stroke="couleur ? couleur : 'url(#page-cagnotte-barre-anneau)'"
              :stroke-width="TRAIT"
              fill="none"
              stroke-linecap="round"
              :stroke-dasharray="CIRCONFERENCE"
              :stroke-dashoffset="decalage"
              class="transition-[stroke-dashoffset] duration-[900ms] ease-out"
            />
          </svg>
          <span class="absolute inset-0 flex items-center justify-center text-[13px] text-(--color-ink)" style="font-weight: 800 !important">
            {{ pourcent }}%
          </span>
        </div>

        <!-- Montant collecté (masqué si la cagnotte le cache) -->
        <div v-if="afficherMontant !== false" class="@container min-w-0 flex-1">
          <!-- Montant sur UNE ligne, en « k », comme l'ancienne barre -->
          <p class="truncate whitespace-nowrap text-[19px] leading-tight text-(--color-ink)">
            <span style="font-weight: 800 !important; letter-spacing: -0.02em">{{ formatAmountCompact(collecte) }}</span>
            <!-- Espaces par marges : ceux du texte sont retirés à la compilation.
                 Place comptée (petit téléphone, démo de la home) : « collectés »
                 s'efface, « 6,9k € sur 30k € » reste lisible en entier. -->
            <span class="ml-1 @max-[13rem]:hidden text-[14px] text-gray-500" style="font-weight: 600 !important">{{ $t("campagne.pageCagnotte.collectes") }}</span>
            <template v-if="objectif > 0">
              <span class="mx-1 text-[14px] text-gray-500" style="font-weight: 600 !important">{{ $t("campagne.pageCagnotte.sur") }}</span>
              <span style="font-weight: 800 !important; letter-spacing: -0.02em">{{ formatAmountCompact(objectif) }}</span>
            </template>
          </p>
          <!-- Plus gros don, comme sur l'ancienne barre -->
          <p v-if="plusGrosDon" class="mt-1 flex items-center gap-1 text-[13px] text-gray-500 whitespace-nowrap">
            <UIcon name="i-lucide-crown" class="size-3.5 shrink-0 text-amber-500" />
            <!-- Nom trop long : coupé avec « … » ; « a donné 1 011 € » reste entier. -->
            <span class="min-w-0 truncate text-(--color-ink)" style="font-weight: 600 !important">{{
              plusGrosDon.is_anonymous ? t("ui.commun.anonyme") : plusGrosDon.name
            }}</span>
            <span class="shrink-0">{{ $t("campagne.pageCagnotte.aDonne") }}</span>
            <span class="shrink-0 text-(--color-ink)" style="font-weight: 600 !important">{{ formatAmountShort(plusGrosDon.amount) }}</span>
          </p>
        </div>

        <span v-if="afficherMontant === false" class="flex-1" />
        <button
          type="button"
          class="shrink-0 size-9 rounded-full bg-white ring-1 ring-gray-200 text-(--color-ink) flex items-center justify-center active:scale-95 transition-transform"
          :aria-label="$t('campagne.pageCagnotte.partager')"
          @click="emit('partager')"
        >
          <UIcon name="i-lucide-share-2" class="size-4" />
        </button>
      </div>

      <!-- Date limite (barre réservée au temps restant, comme sur la carte) -->
      <CampaignCountdown
        v-if="closesAt && endDateLabel"
        compact
        class="mt-3"
        :style="{ '--tp-fin': couleur || 'var(--color-main)' }"
        :closes-at="closesAt"
        :starts-at="cagnotte.date_created ?? null"
        :end-date-label="endDateLabel"
        :days-remaining="cagnotte.days_remaining ?? null"
      />

      <button
        type="button"
        class="mt-3 flex w-full items-center justify-center gap-2 py-3 rounded-full text-white text-[16px] active:scale-[0.98] transition-transform"
        :style="styleBoutonDon"
        style="font-weight: 700 !important"
        @click="ouvrir"
      >
        <UIcon name="i-lucide-heart" class="size-5" />
        {{ $t("campagne.pageCagnotte.faireUnDon") }}
      </button>
        </div>
        <PageCagnotteEtatDon
          v-if="etatDon"
          :etat="etatDon"
          :date-fin="dateFin"
          :slug-association="cagnotte.organization_slug"
          compact
        />
      </div>
    </Transition>

    <!-- PANNEAU : toujours en place, invisible et descendu tant qu'il est fermé -->
    <Teleport v-if="panneauPret" to="body" :disabled="!estIntegree">
      <div
        ref="panneau"
        class="lg:hidden fixed inset-x-0 bottom-0 z-[10011] flex max-h-[94svh] flex-col overflow-hidden rounded-t-[28px] bg-white shadow-[0_-12px_48px_-12px_rgba(12,48,151,0.35)] will-change-transform origin-top"
        :class="ouvert ? '' : 'pointer-events-none'"
        :style="stylePanneau"
        :inert="!ouvert"
        :aria-hidden="!ouvert"
      >
        <div class="overflow-y-auto overscroll-contain">
          <PageCagnotteClavierDon
            :cagnotte="cagnotte"
            :fiscal="fiscal"
            :montants="montants"
            :index-defaut="indexDefaut"
            :impacts="impacts"
            :couleur="couleur"
            @fermer="fermer"
            @glisse="glisse = $event"
            @lache="lache"
          />
        </div>
      </div>
    </Teleport>
  </template>
</template>

<style scoped>
/* TOUJOURS devant le chat (z 50), bouton comme fenêtre ouverte : sur la
   page cagnotte, le chat passe derrière (Karim 2026-10-13). */
.barre-don {
  z-index: 60;
}
/* Dons fermés : le contenu reste visible, flouté et grisé, sous l'état. */
.contenu-voile {
  filter: blur(5px) grayscale(1);
  opacity: 0.75;
  user-select: none;
}
/* Le panneau (z 10011) passe au-dessus de TOUT, header du site compris
   (z 9999, son bouton menu 10002) ; le fond assombri est juste dessous. */
</style>
